import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    restrictToVerticalAxis,
    restrictToWindowEdges,
} from '@dnd-kit/modifiers';
import "./UserList.css"; // Use the same CSS as UserList
import { TEXT_CONTACT, TEXT_DESCRIPTION, TEXT_LOGIN_ID, TEXT_LAST_LOGIN, TEXT_DEVICE_CONFIGURATION, TEXT_PHONE, TEXT_UPDATED_DATE, TEXT_USER_NAME } from 'constants/message';
import { CURRENT_PAGE_DEFAULT, ITEM_NUM_PER_PAGE } from 'constants/config';
import DragDropUserItem from './DragDropUserItem';
import Pagination from 'components/Pagination';
import { apiGetUserLoginHistory } from 'apis/UserApi';
import LoginHistoryPopup from './LoginHistoryPopup';

const formatTimestamp = (timestamp) => {
    const date = new Date(Number(timestamp));
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    const hours = ('0' + date.getHours()).slice(-2);
    const minutes = ('0' + date.getMinutes()).slice(-2);
    const seconds = ('0' + date.getSeconds()).slice(-2);
    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
};

const propTypes = {
    items: PropTypes.array,
    curPage: PropTypes.number,
    onEditItem: PropTypes.func,
    onDeleteItem: PropTypes.func,
    onPageChange: PropTypes.func,
    setSelectedUser: PropTypes.func,
    selectedUser: PropTypes.any,
    onReorder: PropTypes.func, // New prop for handling reorder
    dragDisabled: PropTypes.bool, // New prop to disable dragging
};

const defaultProps = {
    currentUser: null,
    items: [],
    curPage: CURRENT_PAGE_DEFAULT,
    itemNumPerPage: ITEM_NUM_PER_PAGE,
    onEditItem: (item) => { },
    onDeleteItem: (item) => { },
    onPageChange: (newPage) => { },
    setSelectedUser: () => { },
    selectedUser: null,
    onReorder: (reorderedItems) => { }, // Default handler
    dragDisabled: false, // Default to allow dragging
};

const DragDropUserList = (props) => {
    const { currentUser, selectedUser, setSelectedUser, items: propItems, onReorder, dragDisabled } = props;
    const [items, setItems] = useState([]);
    const [loginHistoryPopup, setLoginHistoryPopup] = useState({ isOpen: false, user: null, history: [] });
    const pageNum = !props.items.length ? 1 : Math.ceil(props.items.length / props.itemNumPerPage);
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Minimum distance to start dragging
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );
    const [total, setTotal] = useState(0);

    const handleShowLoginHistory = async (user) => {
        const response = await apiGetUserLoginHistory(user.id);
        const history = response.success ? response.data : [];
        setLoginHistoryPopup({ isOpen: true, user, history });
    };

    const handleCloseLoginHistory = () => {
        setLoginHistoryPopup({ isOpen: false, user: null, history: [] });
    };

    React.useEffect(() => {
        if (props.maxItems) {
            return props.items.length > props.maxItems ? setTotal(props.maxItems) : setTotal(props.items.length);
        } else {
            return setTotal(props.items.length);
        }
    }, [props.items.length, props.maxItems]);

    useEffect(() => {
        if (!selectedUser && currentUser) {
            setSelectedUser({
                id: currentUser.id,
                email: currentUser.email,
                nameList: currentUser?.data?.nameList || [],
                labelArea: currentUser?.data?.labelArea || ""
            });
        }
    }, [currentUser, selectedUser, setSelectedUser]);

    useEffect(() => {
        setItems(propItems);
    }, [propItems]);

    const handleDragEnd = (event) => {
        // Don't process drag end if dragging is disabled
        if (dragDisabled) {
            return;
        }

        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = items.findIndex((item) => item.id === active.id);
            const newIndex = items.findIndex((item) => item.id === over.id);

            const reorderedItems = arrayMove(items, oldIndex, newIndex);
            setItems(reorderedItems);

            // Call the parent component's reorder handler
            onReorder(reorderedItems);
        }
    };

    const renderHeader = () => {
        return <div className='user_list_header_item_wrapper'>
            <div className='user_list_item'>
                <div className='drag_handle_header_space'>
                    {/* Empty space for drag handle */}
                </div>
                <label className='user_list_id_header_cell'>
                    No
                </label>
                <label className='user_list_text_header_cell'>
                    {TEXT_USER_NAME}
                </label>
                <label className='user_list_text_header_cell'>
                    権限
                </label>
                <label className='user_list_text_header_cell'>
                    2段階認証
                </label>
                <label className='user_list_date_header_cell'>
                    {TEXT_LAST_LOGIN}
                </label>
                <label className='user_list_text_header_cell'>
                    {TEXT_LOGIN_ID}
                </label>
                <label className='user_list_text_header_cell'>
                    {TEXT_CONTACT}
                </label>
                <label className='user_list_text_header_cell'>
                    {TEXT_DEVICE_CONFIGURATION}
                </label>
                <label className='user_list_text_header_cell'>
                    {TEXT_PHONE}
                </label>
                <label className='user_list_text_header_cell'>
                    {TEXT_DESCRIPTION}
                </label>
                <label className='user_list_date_header_cell'>
                    {TEXT_UPDATED_DATE}
                </label>
                <div className='user_list_action_cell' />
            </div>
            <div className='user_list_item_separator' />
        </div>;
    };

    const renderList = () => {
        return (
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
            >
                <SortableContext items={items.map(item => item.id)} strategy={verticalListSortingStrategy}>
                    <div className="user_list_container">
                        {renderHeader()}
                        {(props.itemNumPerPage > 0 ? items.slice((props.curPage - 1) * props.itemNumPerPage, (props.curPage - 1) * props.itemNumPerPage + props.itemNumPerPage) : items)
                            .map((item) => (
                                <DragDropUserItem
                                    key={item.id}
                                    item={item}
                                    currentUser={currentUser}
                                    selectedUser={selectedUser}
                                    onEditItem={props.onEditItem}
                                    onDeleteItem={props.onDeleteItem}
                                    setSelectedUser={setSelectedUser}
                                    formatTimestamp={formatTimestamp}
                                    dragDisabled={dragDisabled}
                                    onShowLoginHistory={handleShowLoginHistory}
                                />
                            ))}
                    </div>
                </SortableContext>
            </DndContext>
        );
    };

    return (
        <div className="user_list_wrapper">
            <Pagination
                pageNum={pageNum}
                curPage={props.curPage}
                onPageChange={props.onPageChange}
                itemNumPerPage={props.itemNumPerPage}
                total={total}
                isNew={props.isNew}
                functionBtn={props.functionBtn}
            />
            {renderList()}
            <LoginHistoryPopup
                isOpen={loginHistoryPopup.isOpen}
                onClose={handleCloseLoginHistory}
                user={loginHistoryPopup.user}
                loginHistory={loginHistoryPopup.history}
            />
        </div>
    );
};

DragDropUserList.propTypes = propTypes;
DragDropUserList.defaultProps = defaultProps;
export default DragDropUserList;
